/**
 * FastPassHistory.tsx
 * @description :: This page displays the Fast Pass log history of members
 */
import React, { useEffect, useState } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Center,
  Select,
  Checkbox,
  Pagination,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import classes from "./fastPassHistory.module.css";
import { RowDataFastPass } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { fastPass } from "../types/RowDataTypes";
import { SearchBar } from "../SearchBar/SearchBar";
import Header from "../static/Header";
import getFastPassTable from "../../DataDisplay/DataDisplayTable/FastPassTable";
import { ThProps } from "../TransactionPage/TransactionPage";

export enum ColumnHeaderToFilterPairFastPass {
  LOG_ID = "Log ID",
  NAME = "Name",
  CREATED = "Created At",
  REMARK = "Remark",
}

export const colFilterFastPass = ColumnHeaderToFilterPairFastPass;

export const colFiltersFastPass: { [key in ColumnHeaderToFilterPairFastPass]: string } = {
  [colFilterFastPass.LOG_ID]: "log_id",
  [colFilterFastPass.NAME]: "fullname",
  [colFilterFastPass.CREATED]: "createdAt",
  [colFilterFastPass.REMARK]: "remarks",
};

const allowedSortsFastPass = [
  colFilterFastPass.NAME.toLowerCase(),
  colFilterFastPass.CREATED.toLowerCase(),
];

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <Table.Th>
      {allowedSortsFastPass.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <div className={classes.sortableHeader}>
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </div>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

export function FastPassHistory() {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof RowDataFastPass | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const allowedFilters = [
    colFiltersFastPass[colFilterFastPass.LOG_ID],
    colFiltersFastPass[colFilterFastPass.NAME],
  ];

  const checkboxesFilter: JSX.Element[] = [];
  for (const [key, value] of Object.entries(colFiltersFastPass)) {
    if (allowedFilters.includes(value)) {
      checkboxesFilter.push(<Checkbox value={value} label={key} />);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    fastPass,
    getFastPassTable().create(),
  );

  const totalRows = fetchedData.totalRows;
  const rowData: RowDataFastPass[] | null = fetchedData.data as RowDataFastPass[];
  const isLoading: boolean = fetchedData.isLoading;

  const [rowDataFastPass, setRowDataFastPass] = useState<RowDataFastPass[] | null>(null);

  useEffect(() => {
    setRowDataFastPass(rowData);
  }, [rowData]);

  const setSorting = (field: keyof RowDataFastPass) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage !== currentPage) setCurrentPage(selectedPage);
  };

  const rowsToMap = rowDataFastPass ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "15%" }}>{row.log_id}</Table.Td>
      <Table.Td style={{ width: "15%" }}>{row.fullname}</Table.Td>
      <Table.Td style={{ width: "15%" }}>
        {new Date(row.created_at).toLocaleString("en-MY", {
          timeZone: "Asia/Kuala_Lumpur",
        })}
      </Table.Td>
      <Table.Td style={{ width: "25%" }}>{row.remark}</Table.Td>
    </Table.Tr>
  ));

  return (
    <div className={classes.fastPassHistoryPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Fast Pass History" />
        <div className={classes.searchAndAddContainer}>
          <div style={{ flex: 1 }}>
            <SearchBar
              isLoading={isLoading}
              resetPage={resetPage}
              changeTableState={changeTableState}
              search={search}
              handleSearch={handleSearch}
              setShowFilterOptions={setShowFilterOptions}
              showFilterOptions={showFilterOptions}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              checkboxesFilter={checkboxesFilter}
            />
          </div>
        </div>

        <ScrollArea
          w={
            window.innerWidth -
            document.getElementById("nav-bar")!!.getBoundingClientRect().width
          }
        >
          <div className={classes.parentTable}>
            <Table className={classes.wholetable} highlightOnHover>
              <Table.Thead>
                <Table.Tr className={classes.tableHeaderRow}>
                  <Table.Th style={{ width: "4%" }}>No.</Table.Th>
                  <Th
                    sortable={false}
                    reversed={reverseSortDirection}
                    sorted={sortBy === "log_id"}
                    onSort={() => setSorting("log_id")}
                  >
                    Log ID
                  </Th>
                  <Th
                    sortable={true}
                    reversed={reverseSortDirection}
                    sorted={sortBy === "fullname"}
                    onSort={() => setSorting("fullname")}
                  >
                    Name
                  </Th>
                  <Th
                    sortable={true}
                    reversed={reverseSortDirection}
                    sorted={sortBy === "created_at"}
                    onSort={() => setSorting("created_at")}
                  >
                    Created At
                  </Th>
                  <Th
                    sortable={false}
                    reversed={reverseSortDirection}
                    sorted={sortBy === "remark"}
                    onSort={() => setSorting("remark")}
                  >
                    Remark
                  </Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length > 0 ? (
                  rows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text className={classes.NothingFound}>Nothing found</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </ScrollArea>

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default FastPassHistory;
